import { useState, useEffect, useCallback } from "react";
import {
  Card,
  IndexTable,
  BlockStack,
  SkeletonBodyText,
  IndexFilters,
  Page,
  IndexFiltersMode,
  InlineStack,
  Icon,
  Box,
  Text,
  Button,
  Tooltip,
  IndexFiltersProps,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { IndexTableHeading } from "@medentic/polaris-react/build/ts/src/components/IndexTable/IndexTable";
import { NonEmptyArray } from "@medentic/polaris-react/build/ts/src/types";
import { AppPageWrapper } from "@shared/ui";
import { TabsTableSkeleton } from "@shared/ui";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { ExternalSmallIcon, ShieldCheckMarkIcon } from "@medentic/polaris-icons";
import useLanguages from "@shared/hooks/common/useLanguages";
import { useCountries } from "@shared/hooks/common/useCountries";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { MultiSelect } from "@shared/ui";
import { Event, Instructor } from "@features/events/types";
import { useEvents } from "@features/events/hooks/useEventsDashboard";
import { InstructorFilterOptionsBuilder } from "./InstructorFilterOptionsBuilder";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";

export default function EventInstructors() {
  const { t, i18n } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { GET } = useApiRequest();
  const { languages } = useLanguages();
  const { countries } = useCountries();
  const { metadata } = useEventMetadata();
  const navigate = useNavigate();
  const serializedSearchParams = searchParams.toString();

  // Fetch events for filter
  const { data: allEvents = [] } = useEvents(hcc) as { data: Event[] };
  
  // Tabs
  const tabs = [
    { content: t("instructors.all"), index: 0, id: "all" },
    { content: t("instructors.myInstructors"), index: 1, id: "myInstructors" },
  ];

  // Filters state from URL
  const [selectedTab, setSelectedTab] = useState(() => {
    const status = searchParams.get("status");
    if (status === "myInstructors") return 1;
    return 0;
  });
  const [selectedCountries, setSelectedCountries] = useState<string[]>(
    () => searchParams.get("countries")?.split(",").filter(Boolean) || []
  );
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
    () => searchParams.get("languages")?.split(",").filter(Boolean) || []
  );
  const [selectedSpecializations, setSelectedSpecializations] = useState<
    string[]
  >(
    () => searchParams.get("specializations")?.split(",").filter(Boolean) || []
  );
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>(
    () => searchParams.get("eventIds")?.split(",").filter(Boolean) || []
  );
  const [queryValue, setQueryValue] = useState(
    () => searchParams.get("search") || ""
  );
  const [debouncedQuery, setDebouncedQuery] = useState(
    () => searchParams.get("search") || ""
  );
  const [sortSelected, setSortSelected] = useState<string[]>(() => {
    const sortParam = searchParams.get("sort");
    if (sortParam) return [sortParam];
    
    // Check localStorage for saved sorting preference
    const savedSort = localStorage.getItem("events_instructors_sorting");
    return savedSort ? [savedSort] : ["name asc"];
  });
  const [mode, setMode] = useState(IndexFiltersMode.Default);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  // Sort options
  const sortOptions: IndexFiltersProps['sortOptions'] = [
    {label: t('instructors.sort_by.created'), value: 'created asc', directionLabel: t('common.oldestToNewest')},
    {label: t('instructors.sort_by.created'), value: 'created desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('instructors.sort_by.modified'), value: 'updated asc', directionLabel: t('common.oldestToNewest')},
    {label: t('instructors.sort_by.modified'), value: 'updated desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('instructors.sort_by.name'), value: 'name asc', directionLabel: t('common.AtoZ')},
    {label: t('instructors.sort_by.name'), value: 'name desc', directionLabel:t('common.ZtoA')},
  ];

  // Save sorting preference to localStorage
  useEffect(() => {
    if (sortSelected[0]) {
      localStorage.setItem("events_instructors_sorting", sortSelected[0]);
    }
  }, [sortSelected]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(queryValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [queryValue]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTab === 1) params.set("status", "myInstructors");
    if (selectedCountries.length > 0)
      params.set("countries", selectedCountries.join(","));
    if (selectedLanguages.length > 0)
      params.set("languages", selectedLanguages.join(","));
    if (selectedSpecializations.length > 0)
      params.set("specializations", selectedSpecializations.join(","));
    if (selectedEventIds.length > 0)
      params.set("eventIds", selectedEventIds.join(","));
    if (debouncedQuery) params.set("search", debouncedQuery);
    if (sortSelected[0]) 
      params.set("sort", sortSelected[0]);
    setSearchParams(params);
  }, [
    selectedTab,
    selectedCountries,
    selectedLanguages,
    selectedSpecializations,
    selectedEventIds,
    debouncedQuery,
    sortSelected,
    setSearchParams,
  ]);

  // Fetch instructors
  const {
    data: instructors = [],
    isLoading,
    isFetching,
  } = useQuery<Instructor[]>({
    queryKey: [
      "event-instructors",
      hcc,
      selectedTab,
      selectedCountries,
      selectedLanguages,
      selectedSpecializations,
      selectedEventIds,
      debouncedQuery,
      sortSelected,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedTab === 1) params.append("usedBefore", "true");
      if (selectedCountries.length > 0) {
        params.append("countries", selectedCountries.join(","));
      }
      if (selectedLanguages.length > 0) {
        params.append("languages", selectedLanguages.join(","));
      }
      if (selectedSpecializations.length > 0) {
        params.append("categories", selectedSpecializations.join(","));
      }
      if (selectedEventIds.length > 0) {
        params.append("eventIds", selectedEventIds.join(","));
      }
      if (debouncedQuery) params.append("query", debouncedQuery);
      if (sortSelected[0]) params.append("sort", sortSelected[0]);
      
      return GET(`/api/healthcare-companies/${hcc}/e/instructors?${params}`);
    },
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });

  // Table headings
  const headings: NonEmptyArray<IndexTableHeading> = [
    { title: t("instructors.name") || "Name" },
    { title: t("instructors.city") || "City" },
    { title: t("instructors.country") || "Country" },
    { title: t("instructors.languages") || "Languages" },
    { title: "", alignment: "end" },
  ];
  const instructorProfileURL = metadata?.instructorProfileURL;

  const handlePreview = useCallback(
    (i: Instructor) => {
      if (i.slug && instructorProfileURL) {
        window.open(`${instructorProfileURL}${i.slug}`, "_blank");
      }
    },
    [instructorProfileURL]
  );

  // Render function for individual instructor row
  const renderInstructorRow = useCallback(
    (instructor: Instructor, index: number) => {
      return (
        <IndexTable.Row
          id={instructor.id}
          key={instructor.id}
          position={index}
          onClick={() =>
            navigate(
              `/e/${hcc}/instructors/${instructor.id}${
                serializedSearchParams ? `?${serializedSearchParams}` : ""
              }`
            )
          }
        >
          <IndexTable.Cell>
            <InlineStack gap="200" blockAlign="center">
              <EventsDefaultAvatar 
                name={instructor.nameWithPrefix || ""} 
                singleImage={instructor.avatar}
              />
              <span>{instructor.nameWithPrefix}</span>
              {instructor.verified && (
                <Box>
                  <Icon source={ShieldCheckMarkIcon} tone="success" />
                </Box>
              )}
            </InlineStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            { getLocalizedText(instructor.geoAddress?.city) || "-"}
          </IndexTable.Cell>
          <IndexTable.Cell>
            {getLocalizedText(instructor.geoAddress?.country) || "-"}
          </IndexTable.Cell>
          {/* Languages */}
          <IndexTable.Cell>
            {instructor.languageCodes && instructor.languageCodes.length > 0 ? (
              (() => {
                const instructorLanguages = instructor.languageCodes
                  .map((code) => languages.find((l) => l.iso === code))
                  .filter(Boolean);
                
                const visibleLanguages = instructorLanguages.slice(0, 3);
                const hasMore = instructorLanguages.length > 3;
                
                const tooltipContent = instructorLanguages
                  .map((lang) => lang?.englishLabel || "")
                  .join(", ");

                return (
                  <Tooltip content={tooltipContent}>
                    <InlineStack gap="100" blockAlign="center" wrap={false}>
                      {visibleLanguages.map((language) => (
                        <Box key={language?.iso}>
                          {language?.svg && (
                            <img
                              src={language.svg}
                              alt={language.localLabel}
                              style={{ width: "20px", height: "15px" }}
                            />
                          )}
                        </Box>
                      ))}
                      {hasMore && (
                        <Text as="span" variant="bodyMd">
                          ...
                        </Text>
                      )}
                    </InlineStack>
                  </Tooltip>
                );
              })()
            ) : (
              "-"
            )}
          </IndexTable.Cell>

          <IndexTable.Cell>
            <InlineStack gap="200" align="end">
              <Box
                opacity={hoveredRow === instructor.id ? "1" : "0"}
                paddingInlineEnd="100"
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Button
                    onClick={() => {
                      handlePreview(instructor);
                    }}
                    variant="tertiary"
                    icon={ExternalSmallIcon}
                  >
                  </Button>
                </div>
              </Box>
            </InlineStack>
          </IndexTable.Cell>
        </IndexTable.Row>
      );
    },
    [navigate, hcc, serializedSearchParams, languages, hoveredRow, handlePreview]
  );

  // Table rows
  const rowMarkup = instructors.map((instructor: Instructor, index: number) =>
    renderInstructorRow(instructor, index)
  );

  // Prepare choices for filters based on actual instructor data
  const filterOptionsBuilder = new InstructorFilterOptionsBuilder(
    instructors,
    countries,
    languages,
    metadata?.categories || []
  );

  const { countries: countryChoices, languages: languageChoices, specializations: specializationChoices } = 
    filterOptionsBuilder.getAllOptions();

  const eventChoices = allEvents.map((event: Event) => ({
    label:
      event.title?.[i18n.language] ||
      Object.values(event.title || {})[0] ||
      event.id,
    value: event.id,
  }));

  // Filters (dropdowns, populated with actual data)
  const filters = [
    {
      key: "country",
      label: t("instructors.country"),
      filter: (
        <MultiSelect
          label={t("instructors.country")}
          placeholder={t("instructors.country")}
          options={countryChoices}
          selected={selectedCountries}
          onChange={setSelectedCountries}
        />
      ),
      pinned: true,
    },
    {
      key: "language",
      label: t("instructors.languages"),
      filter: (
        <MultiSelect
          label={t("instructors.languages")}
          placeholder={t("instructors.languages")}
          options={languageChoices}
          selected={selectedLanguages}
          onChange={setSelectedLanguages}
        />
      ),
      pinned: true,
    },
    {
      key: "specialization",
      label: t("instructors.specialization"),
      filter: (
        <MultiSelect
          label={t("instructors.specialization")}
          placeholder={t("instructors.specialization")}
          options={specializationChoices}
          selected={selectedSpecializations}
          onChange={setSelectedSpecializations}
        />
      ),
      pinned: true,
    },
    {
      key: "event",
      label: t("instructors.event"),
      filter: (
        <MultiSelect
          label={t("instructors.event")}
          placeholder={t("instructors.event")}
          options={eventChoices}
          selected={selectedEventIds}
          onChange={setSelectedEventIds}
        />
      ),
      pinned: true,
    },
  ];

  // Applied filters with proper labels
  const appliedFilters: Array<{
    key: string;
    label: string;
    onRemove: () => void;
  }> = [];

  selectedCountries.forEach((countryValue) => {
    const countryLabel =
      countryChoices.find(
        (c: { label: string; value: string }) => c.value === countryValue
      )?.label || countryValue;
    appliedFilters.push({
      key: "country",
      label: `${t("instructors.country")}: ${countryLabel}`,
      onRemove: () =>
        setSelectedCountries((prev) => prev.filter((c) => c !== countryValue)),
    });
  });

  selectedLanguages.forEach((languageValue) => {
    const languageChoice = languageChoices.find(
      (l) => l.value === languageValue
    );
    const languageLabel = languageChoice?.label || languageValue;
    appliedFilters.push({
      key: "language",
      label: `${t("instructors.languages")}: ${languageLabel}`,
      onRemove: () =>
        setSelectedLanguages((prev) => prev.filter((l) => l !== languageValue)),
    });
  });

  selectedSpecializations.forEach((specializationValue) => {
    const specializationLabel =
      specializationChoices.find((s) => s.value === specializationValue)
        ?.label || specializationValue;
    appliedFilters.push({
      key: "specialization",
      label: `${t("instructors.specialization")}: ${specializationLabel}`,
      onRemove: () =>
        setSelectedSpecializations((prev) =>
          prev.filter((s) => s !== specializationValue)
        ),
    });
  });

  selectedEventIds.forEach((eventValue) => {
    const eventLabel =
      eventChoices.find((e) => e.value === eventValue)?.label || eventValue;
    appliedFilters.push({
      key: "event",
      label: `${t("instructors.event")}: ${eventLabel}`,
      onRemove: () =>
        setSelectedEventIds((prev) => prev.filter((e) => e !== eventValue)),
    });
  });

  const isInitialLoading = isLoading && instructors.length === 0;

  if (isInitialLoading) {
    return (
      <AppPageWrapper>
        <Page
          fullWidth
          title={t("instructors.title")}
          primaryAction={{
            content: t("instructors.addInstructor"),
          }}
        >
          <TabsTableSkeleton />
        </Page>
      </AppPageWrapper>
    );
  }

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        title={t("instructors.title")}
        primaryAction={{
          content: t("instructors.addInstructor"),
          onAction: () => navigate(`/e/${hcc}/instructors/new`),
        }}
      >
        <Card padding="0">
          <BlockStack gap="0">
            <IndexFilters
              tabs={tabs}
              selected={selectedTab}
              onSelect={setSelectedTab}
              queryValue={queryValue}
              queryPlaceholder={t("instructors.searchPlaceholder")}
              onQueryChange={setQueryValue}
              onQueryClear={() => setQueryValue("")}
              filters={filters}
              cancelAction={{
                onAction: () => {
                  setSelectedTab(0);
                  setSelectedCountries([]);
                  setSelectedLanguages([]);
                  setSelectedSpecializations([]);
                  setSelectedEventIds([]);
                  setQueryValue("");
                  setSortSelected(["name asc"]);
                },
                disabled: false,
                loading: false,
              }}
              appliedFilters={appliedFilters}
              onClearAll={() => {
                setSelectedTab(0);
                setSelectedCountries([]);
                setSelectedLanguages([]);
                setSelectedSpecializations([]);
                setSelectedEventIds([]);
                setQueryValue("");
                setSortSelected(["name asc"]);
              }}
              sortOptions={sortOptions}
              sortSelected={sortSelected}
              onSort={setSortSelected}
              mode={mode}
              setMode={setMode}
              loading={isFetching && instructors.length > 0}
            />
            <div
              onMouseMove={(e) => {
                e.stopPropagation();
                const target = e.target as HTMLElement;
                const id = target.closest("tr")?.getAttribute("id") || null;
                setHoveredRow(id || null);
              }}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <IndexTable
                resourceName={{
                  singular: t("instructors.instructor"),
                  plural: t("instructors.instructors"),
                }}
                itemCount={instructors.length}
                selectable={false}
                headings={headings}
              >
                {isLoading
                  ? [...Array(5)].map((_, i) => (
                      <IndexTable.Row id={`skeleton-${i}`} key={i} position={i}>
                        {headings.map((_, j) => (
                          <IndexTable.Cell key={j}>
                            <SkeletonBodyText lines={1} />
                          </IndexTable.Cell>
                        ))}
                      </IndexTable.Row>
                    ))
                  : rowMarkup}
              </IndexTable>
            </div>
          </BlockStack>
        </Card>
      </Page>
    </AppPageWrapper>
  );
}
